import {
  getEconomyTemplate,
  updateEconomyTemplate,
  updateEconomyTemplateAttributes,
} from '@/api/economyTemplates';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types';
import { EconomyTemplateAttribute, EconomyTemplateDetail } from '@/types/economyTemplate';
import {
  ECONOMY_TEMPLATE_VISIBILITY,
  getEconomyTemplateTypeLabel,
} from '@/utils/economyTemplateLabels';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import {
  EconomyTemplateParamsForm,
  formatFormRateForStorage,
  formatStoredRateForForm,
  RATE_FIELD_NAMES,
} from './EconomyTemplateParamsForm';

interface TemplateDetailModalProps {
  open: boolean;
  templateId: string | null;
  onClose: () => void;
  onTemplateUpdated?: () => void;
}

const CURRENCIES = ['USD', 'EUR', 'GBP'];

function attributesToFormValues(attributes: EconomyTemplateAttribute[]): Record<string, string> {
  const values: Record<string, string> = {};
  for (const attr of attributes) {
    const raw = attr.value ?? '';
    values[attr.id] = RATE_FIELD_NAMES.has(attr.name)
      ? formatStoredRateForForm(attr.name, raw)
      : raw;
  }
  return values;
}

const TemplateDetailModal: React.FC<TemplateDetailModalProps> = ({
  open,
  onClose,
  templateId,
  onTemplateUpdated,
}) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === UserRole.ADMIN || user?.role === 'admin';

  const [template, setTemplate] = useState<EconomyTemplateDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [attributes, setAttributes] = useState<EconomyTemplateAttribute[]>([]);
  const [paramValues, setParamValues] = useState<Record<string, string>>({});

  const [editData, setEditData] = useState({
    name: '',
    description: '',
    currency: 'USD',
    unit_id: 1,
    visibility_status_id: ECONOMY_TEMPLATE_VISIBILITY.PRIVATE,
  });

  useEffect(() => {
    if (open && templateId) {
      setEditMode(false);
      loadTemplate();
    }
  }, [open, templateId]);

  const loadTemplate = async () => {
    if (!templateId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getEconomyTemplate(templateId);
      setTemplate(data);
      setAttributes([...data.attributes]);
      setParamValues(attributesToFormValues(data.attributes));
      setEditData({
        name: data.name,
        description: data.description ?? '',
        currency: data.currency,
        unit_id: data.unit_id,
        visibility_status_id: data.visibility_status_id ?? ECONOMY_TEMPLATE_VISIBILITY.PRIVATE,
      });
    } catch {
      setError('Failed to load template details');
    } finally {
      setLoading(false);
    }
  };

  const handleParamChange = (attributeId: string, value: string) => {
    setParamValues((prev) => ({ ...prev, [attributeId]: value }));
  };

  const handleSave = async () => {
    if (!templateId || !template) return;

    setLoading(true);
    setError(null);

    try {
      await updateEconomyTemplate(templateId, {
        name: editData.name,
        description: editData.description || null,
        currency: editData.currency,
        unit_id: editData.unit_id,
        visibility_status_id: editData.visibility_status_id,
      });

      const attrById = new Map(attributes.map((a) => [a.id, a]));
      await updateEconomyTemplateAttributes(templateId, {
        attributes: Object.entries(paramValues)
          .filter(([, value]) => value.trim() !== '')
          .map(([attributeId, value]) => {
            const meta = attrById.get(attributeId);
            const stored = meta && RATE_FIELD_NAMES.has(meta.name)
              ? formatFormRateForStorage(meta.name, value)
              : value.trim();
            return { economy_attribute_id: attributeId, value: stored };
          }),
      });

      const reloaded = await getEconomyTemplate(templateId);
      setTemplate(reloaded);
      setAttributes([...reloaded.attributes]);
      setParamValues(attributesToFormValues(reloaded.attributes));
      setEditMode(false);
      onTemplateUpdated?.();
    } catch {
      setError('Failed to update template');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (!template) return;

    setEditData({
      name: template.name,
      description: template.description ?? '',
      currency: template.currency,
      unit_id: template.unit_id,
      visibility_status_id: template.visibility_status_id ?? ECONOMY_TEMPLATE_VISIBILITY.PRIVATE,
    });
    setParamValues(attributesToFormValues(template.attributes));
    setEditMode(false);
  };

  const handleClose = () => {
    setTemplate(null);
    setAttributes([]);
    setParamValues({});
    setEditMode(false);
    setError(null);
    onClose();
  };

  const getVisibilityLabel = (visibilityStatusId: string) => {
    if (visibilityStatusId === ECONOMY_TEMPLATE_VISIBILITY.PUBLIC) return 'Public';
    if (visibilityStatusId === ECONOMY_TEMPLATE_VISIBILITY.COMPANY_PUBLIC) return 'Company Public';
    return 'Private';
  };

  const getVisibilityColor = (visibilityStatusId: string): 'success' | 'info' | 'default' => {
    if (visibilityStatusId === ECONOMY_TEMPLATE_VISIBILITY.PUBLIC) return 'success';
    if (visibilityStatusId === ECONOMY_TEMPLATE_VISIBILITY.COMPANY_PUBLIC) return 'info';
    return 'default';
  };

  const canEditTemplate = template
    ? template.organization_id === user?.organization_id ||
      (template.visibility_status_id === ECONOMY_TEMPLATE_VISIBILITY.PUBLIC && isSuperAdmin)
    : false;

  if (!templateId && !open) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {editMode ? 'Edit Economy Template' : 'Economy Template Details'}
          </Typography>
          {template && (
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <Chip
                icon={
                  editData.visibility_status_id === ECONOMY_TEMPLATE_VISIBILITY.PUBLIC ? (
                    <span>🌐</span>
                  ) : editData.visibility_status_id === ECONOMY_TEMPLATE_VISIBILITY.COMPANY_PUBLIC ? (
                    <span>🏢</span>
                  ) : (
                    <span>🔒</span>
                  )
                }
                label={getVisibilityLabel(editData.visibility_status_id)}
                color={getVisibilityColor(editData.visibility_status_id)}
                size="small"
              />
              <Chip
                label={getEconomyTemplateTypeLabel(template.template_type)}
                color="primary"
                size="small"
              />
              <Chip label={editData.currency} size="small" variant="outlined" />
            </Box>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && !template ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : template ? (
          <Box>
            <Typography variant="h6" gutterBottom>
              Template Information
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Template Name"
                  value={editData.name}
                  onChange={(e) => setEditData((prev) => ({ ...prev, name: e.target.value }))}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth disabled>
                  <InputLabel>Template type</InputLabel>
                  <Select
                    value={template.template_type}
                    label="Template type"
                  >
                    <MenuItem value="field">Field economics</MenuItem>
                    <MenuItem value="layer">Layer economics</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth disabled={!editMode}>
                  <InputLabel>Visibility</InputLabel>
                  <Select
                    value={editData.visibility_status_id}
                    label="Visibility"
                    onChange={(e) =>
                      setEditData((prev) => ({
                        ...prev,
                        visibility_status_id: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value={ECONOMY_TEMPLATE_VISIBILITY.PRIVATE}>Private</MenuItem>
                    <MenuItem value={ECONOMY_TEMPLATE_VISIBILITY.COMPANY_PUBLIC}>
                      Company Public
                    </MenuItem>
                    {isSuperAdmin && (
                      <MenuItem value={ECONOMY_TEMPLATE_VISIBILITY.PUBLIC}>Public</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth disabled={!editMode}>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={editData.currency}
                    label="Currency"
                    onChange={(e) =>
                      setEditData((prev) => ({ ...prev, currency: e.target.value }))
                    }
                  >
                    {CURRENCIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth disabled={!editMode}>
                  <InputLabel>Production units</InputLabel>
                  <Select
                    value={editData.unit_id}
                    label="Production units"
                    onChange={(e) =>
                      setEditData((prev) => ({ ...prev, unit_id: Number(e.target.value) }))
                    }
                  >
                    <MenuItem value={1}>Tons</MenuItem>
                    <MenuItem value={2}>Barrels</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  placeholder="Describe when and how to use this template"
                  multiline
                  minRows={3}
                  value={editData.description}
                  onChange={(e) =>
                    setEditData((prev) => ({ ...prev, description: e.target.value }))
                  }
                  disabled={!editMode}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom>
              Template Metadata
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Author"
                  value={template.author_name || 'Unknown'}
                  disabled
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Editor"
                  value={template.editor_name || 'Unknown'}
                  disabled
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Created"
                  value={new Date(template.created_at).toLocaleDateString()}
                  disabled
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Updated"
                  value={new Date(template.updated_at).toLocaleDateString()}
                  disabled
                  size="small"
                />
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom>
              Template Parameters
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Rates are entered as percent. Per-oil values use the selected production units.
              Fields marked with * are required for economics calculation.
            </Typography>

            <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
              <EconomyTemplateParamsForm
                attributes={attributes}
                templateType={template.template_type}
                unitId={editData.unit_id}
                values={paramValues}
                onChange={handleParamChange}
                disabled={!editMode}
              />
            </Box>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>

        {template && (
          <>
            {editMode ? (
              <>
                <Button onClick={handleCancel} disabled={loading}>
                  Cancel
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={loading}>
                  Save
                </Button>
              </>
            ) : (
              canEditTemplate && (
                <Button onClick={() => setEditMode(true)} variant="contained">
                  Edit
                </Button>
              )
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TemplateDetailModal;
